/**
 * Subscription-tier helpers.
 *
 * The four tiers, and the ONLY four. Since the 2026-08-10 rename the storage
 * key and the name customers see are THE SAME STRING, everywhere — in Stripe,
 * in the database enum, and in this file:
 *
 *   key           name        price
 *   'free'        Free        £0
 *   'pro'         Pro         £39.99/mo
 *   'auto'        Auto        £79.99/mo
 *   'full_auto'   Full Auto   £249.99/mo
 *
 * That was not always true, and the history is worth keeping because it cost
 * real money. `pro` used to be stored as `unlimited` and `auto` as `autopilot`.
 * Because the obvious string for the £39.99 plan was therefore free, a legacy
 * alias claimed "pro" and pointed it at `autopilot` — £79.99. Anything sending
 * that string was billed double (audit S-05). The alias was deleted, and then
 * the keys were renamed so the collision could not recur.
 *
 * Migration 20260810210000 replaced the `subscription_tier` enum, so
 * `unlimited`, `autopilot` and `enterprise` are no longer writable values at
 * all. Historic code also tested for an `enterprise` tier that no database ever
 * held, which meant white-labelling never actually engaged — always resolve tier
 * questions through the helpers below rather than comparing strings inline.
 */

export type SubscriptionTier = 'free' | 'pro' | 'auto' | 'full_auto' | (string & {});

/** Paid tiers where ForgeDash branding is removed from invoices/quotes. */
const WHITE_LABEL_TIERS = ['auto', 'full_auto'];

/**
 * Pre-rename keys, accepted on READ only.
 *
 * A browser holding a cached bundle, or a row read before the migration
 * applied, can still surface the old string. Mapping it costs nothing and is
 * unambiguous — same plan, same price — where the deleted S-05 alias mapped one
 * string onto two different prices.
 *
 * Nothing writes these. Safe to delete once no client older than 2026-08-10 can
 * be running.
 */
const LEGACY_TIER_KEYS: Record<string, SubscriptionTier> = {
    unlimited: 'pro',
    autopilot: 'auto',
};

/**
 * Canonical tier key for a value that may be historic.
 *
 * Use this before ANY tier comparison. Comparing a raw column value against
 * 'auto' silently returns false for a row still reading `autopilot`, which
 * presents as a paying customer losing a feature they are entitled to.
 */
export const canonicalTier = (tier?: string | null): SubscriptionTier | null => {
    if (!tier) return null;
    const key = tier.trim().toLowerCase();
    return LEGACY_TIER_KEYS[key] ?? key;
};

/**
 * True when the org's plan removes the "Powered by ForgeDash" branding.
 * Branding STAYS on `free` and `pro`; it disappears from `auto` (£79.99) upward.
 */
export const isWhiteLabelTier = (tier?: string | null): boolean => {
    const key = canonicalTier(tier);
    return !!key && WHITE_LABEL_TIERS.includes(key);
};

/**
 * True when the org may store/display bank payment details on invoices.
 * Bank details are a paid feature — blocked on the free tier.
 */
export const canUseBankDetails = (tier?: string | null): boolean => {
    const key = canonicalTier(tier);
    return !!key && key !== 'free';
};

const TIER_DISPLAY_NAMES: Record<string, string> = {
    free: 'Free',
    pro: 'Pro',
    auto: 'Auto',
    full_auto: 'Full Auto',
};

/**
 * The plan name to SHOW a customer.
 *
 * Several places rendered the raw column with a `capitalize` class, which was
 * wrong twice over: before the rename it displayed "Unlimited" for a plan sold
 * as Pro, and `full_auto` renders as "Full_auto" no matter what CSS is applied
 * to it. Any tier shown in the UI should come through here.
 *
 * An unrecognised key falls back to a tidied version of itself rather than
 * blank — a surprising plan name is far better than an empty "Current Plan".
 */
export const tierDisplayName = (tier?: string | null): string => {
    const key = canonicalTier(tier);
    if (!key) return 'Free';
    return TIER_DISPLAY_NAMES[key]
        ?? key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};
